import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Disclosure } from '@headlessui/react';
import { 
  Bars3Icon, 
  XMarkIcon,
  HomeIcon,
  TrophyIcon,
  UserGroupIcon,
  CalendarIcon,
  ShieldCheckIcon,
  PhoneIcon,
  LockClosedIcon
} from '@heroicons/react/24/outline';

const ModernHeader: React.FC = () => {
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  const navigation = [
    { name: 'Startseite', href: '/', icon: HomeIcon },
    { name: 'Sportarten', href: '/trainings', icon: TrophyIcon },
    { name: 'Verein', href: '/about', icon: UserGroupIcon },
    { name: 'Termine', href: '/termine', icon: CalendarIcon },
    { name: 'Platzordnung', href: '/platzordnung', icon: ShieldCheckIcon },
    { name: 'Kontakt', href: '/kontakt', icon: PhoneIcon },
  ];

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const isActive = (path: string) => location.pathname === path;

  return (
    <Disclosure as="nav" className={`fixed w-full z-50 transition-all duration-300 ${
      scrolled 
        ? 'bg-white/95 dark:bg-gray-900/95 backdrop-blur-md shadow-lg' 
        : 'bg-transparent'
    }`}>
      {({ open }) => (
        <>
          <div className="container mx-auto px-4 max-w-7xl">
            <div className="relative flex h-16 lg:h-20 items-center justify-between">
              {/* Logo */}
              <div className="flex items-center">
                <Link to="/" className="flex items-center space-x-3 group">
                  <motion.img
                    whileHover={{ rotate: 360 }}
                    transition={{ duration: 0.5 }}
                    className="h-10 w-10 lg:h-12 lg:w-12"
                    src="/images/common/HSV_Logo.jpg"
                    alt="HSV Adlershof"
                  />
                  <div>
                    <p className="text-xl sm:text-lg font-bold text-primary-600 dark:text-primary-400"><span className="sm:hidden">HSV-Adlershof e.V.</span><span className="hidden sm:inline">HSV Berlin-Adlershof e.V.</span></p>
                    <p className="hidden sm:block text-xs text-gray-600 dark:text-gray-400">Hundesportverein seit 1948</p>
                  </div>
                </Link>
              </div>

              {/* Desktop Navigation */}
              <div className="hidden lg:flex items-center space-x-1">
                {navigation.map((item) => {
                  const Icon = item.icon;
                  return (
                    <Link
                      key={item.name}
                      to={item.href}
                      className={`group relative px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                        isActive(item.href)
                          ? 'text-primary-600 dark:text-primary-400'
                          : 'text-gray-700 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400'
                      }`}
                    >
                      <span className="flex items-center space-x-2">
                        <Icon className="w-4 h-4" />
                        <span>{item.name}</span>
                      </span>
                      {isActive(item.href) && (
                        <motion.div
                          layoutId="navbar-indicator"
                          className="absolute inset-x-0 bottom-0 h-0.5 bg-gradient-to-r from-primary-500 to-secondary-500"
                          transition={{ type: "spring", bounce: 0.25, duration: 0.5 }}
                        />
                      )}
                    </Link>
                  );
                })}
              </div>

              {/* Right side buttons */}
              <div className="flex items-center space-x-3">
                {/* Member Login Button */}
                <a
                  href="https://intern.hsv-adlershof.berlin/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hidden lg:flex items-center space-x-2 px-4 py-2 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg transition-colors group"
                  aria-label="Mitglieder Login"
                >
                  <LockClosedIcon className="h-4 w-4 text-gray-600 dark:text-gray-400 group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors" />
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300 group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors">
                    Mitglieder
                  </span>
                </a>

                {/* Contact Button - Desktop */}
                <Link
                  to="/kontakt"
                  className="hidden lg:flex items-center justify-center p-2 bg-primary-600 hover:bg-primary-700 rounded-lg transition-colors"
                  aria-label="Kontakt"
                >
                  <PhoneIcon className="h-5 w-5 text-white" />
                </Link>

                {/* Mobile menu button */}
                <Disclosure.Button className="lg:hidden p-2 rounded-lg bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors">
                  <span className="sr-only">Menü öffnen</span>
                  {open ? (
                    <XMarkIcon className="h-6 w-6 text-gray-700 dark:text-gray-300" />
                  ) : (
                    <Bars3Icon className="h-6 w-6 text-gray-700 dark:text-gray-300" />
                  )}
                </Disclosure.Button>
              </div>
            </div>
          </div>

          {/* Mobile menu */}
          <AnimatePresence>
            {open && (
              <Disclosure.Panel
                static
                as={motion.div}
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="lg:hidden bg-white dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700"
              >
                <div className="container-custom py-4 space-y-1">
                  {navigation.map((item) => {
                    const Icon = item.icon;
                    return (
                      <Disclosure.Button
                        key={item.name}
                        as={Link}
                        to={item.href}
                        className={`flex items-center space-x-3 w-full px-4 py-3 rounded-lg text-base font-medium transition-colors ${
                          isActive(item.href)
                            ? 'bg-gradient-to-r from-primary-50 to-secondary-50 text-primary-600 dark:from-primary-900/30 dark:to-secondary-900/30 dark:text-primary-400'
                            : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'
                        }`}
                      >
                        <Icon className="w-5 h-5" />
                        <span>{item.name}</span>
                      </Disclosure.Button>
                    );
                  })}
                  
                  {/* Mobile: Member Login */}
                  <a
                    href="https://intern.hsv-adlershof.berlin/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center space-x-3 w-full px-4 py-3 rounded-lg text-base font-medium transition-colors bg-gray-50 dark:bg-gray-800 text-gray-700 dark:text-gray-300"
                  >
                    <LockClosedIcon className="w-5 h-5" />
                    <span>Mitglieder Login</span>
                  </a>
                  
                </div>
              </Disclosure.Panel>
            )}
          </AnimatePresence>
        </>
      )}
    </Disclosure>
  );
};

export default ModernHeader;